import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Typography,
  Divider,
  AppBar,
  Toolbar,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { logoutUser } from '../../store/reducers/auth';
import { APIkey, Billing, Contactus, Logout, Profile } from '../../assets';
import { stringAvatar } from '../../utils';
import { Wrapper } from '../wrapper';
import { totalCreditsAndRemaining } from '../../services/creditsService';
import { logoutApi } from "../../services/authService";

// Constants
const DRAWER_WIDTH = 280;
const DRAWER_COLLAPSED_WIDTH = 90;

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  gap: "1rem",
  padding: theme.spacing(2, 6, 1, 3),
}));

export const Navbar = ({ open, setOpen }) => {
  const credit = useSelector((state) => state?.auth?.credits);
  const [anchorEl, setAnchorEl] = useState(null);
  const [remainingCredits, setRemainingCredits] = useState(credit || "NA");
  const activeMenuLabel = useSelector((state) => state?.ui?.activeMenuLabel);
  const dashboardHeader = useSelector(
    (state) => state.ui.dashboardHeader
  );
  
  useEffect(() => {
    setRemainingCredits(parseFloat(Number(credit).toFixed(2)));
  }, [credit]);

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const role = user?.role;
  const open_menu = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await totalCreditsAndRemaining();
        setRemainingCredits(
          parseFloat(Number(res?.data?.credit?.remainingCredit).toFixed(2))
        );
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const theme = useTheme();
  const navigate = useNavigate();

  const handleLogout = useCallback(async (currentUser) => {
    const res = await logoutApi(currentUser);
    dispatch(logoutUser());
    navigate("/login");
  }, [dispatch, navigate]);

  const USER_MENU = useMemo(
    () =>
      [
        {
          key: "profile",
          to: "/profile",
          image: Profile,
          label: "Profile",
        },
        role?.toLowerCase() !== "vkyc auditor" && {
          key: "billing",
          to: "/billing",
          image: Billing,
          label: "Billing",
        },
        { key: "divider", divider: true },
        {
          key: "logout",
          onClick: () => handleLogout(user),
          image: Logout,
          label: "Logout",
        },
      ].filter(Boolean),
    [handleLogout, role]
  );

  const { pathname } = useLocation();

  useEffect(() => {
    const handleRouteChange = () => {
      setAnchorEl(null);
    };
    const unlisten = () => {
      return;
    };

    handleRouteChange();
    return unlisten;
  }, [pathname]);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <AppBar
      position="fixed"
      sx={{
        background: "#ffffff",
        boxShadow:
          "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        zIndex: theme.zIndex.drawer + 1,
        width: `calc(100% - ${open ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH}px)`,
        [theme.breakpoints.down("sm")]: {
          width: "100%",
          margin: "0",
          padding: "0",
          zIndex: theme.zIndex.drawer,
        },
        [theme.breakpoints.up("sm")]: {
          width: "100%",
          margin: "0",
          padding: "0",
          zIndex: theme.zIndex.drawer,
        },
      }}
    >
      <Wrapper>
        <Toolbar
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            p: "0px !important",
            minHeight: { xs: "70px", md: "80px" },
            px: { xs: 2, md: 3 },
          }}
        >
          {isMobile && (
            <IconButton
              aria-label="open drawer"
              edge="start"
              onClick={() => setOpen(!open)}
              sx={{ mr: 2 }}
            >
              <MenuIcon sx={{ fontSize: "25px", color: "#1976d2" }} />
            </IconButton>
          )}

            {/* LEFT TEXT AFTER SIDEBAR */}
            <Box
            sx={{
              display: "flex",
              alignItems: "center",
              ml: open
                ? `${DRAWER_WIDTH + 25}px`
                : `${DRAWER_COLLAPSED_WIDTH + 25}px`,
              transition: "0.3s",
              "@media (max-width: 576px)": {
                    marginLeft: "0px",
                  },
            }}
          >
            {dashboardHeader ? (
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontWeight: 700,
                      color: "#020817",
                    }}
                  >
                    {dashboardHeader.companyName}
                  </Typography>

                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.25,
                      fontSize: "12px",
                      fontWeight: 700,
                      borderRadius: "999px",
                      bgcolor:
                        dashboardHeader.status?.toLowerCase() === "active"
                          ? "#DCFCE7"
                          : "#E5E7EB",
                      color:
                        dashboardHeader.status?.toLowerCase() === "active"
                          ? "#166534"
                          : "#374151",
                      textTransform: "capitalize",
                    }}
                  >
                    {dashboardHeader.status}
                  </Box>
                </Stack>

                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#64748b",
                    lineHeight: 1.3,
                  }}
                >
                  {dashboardHeader.apisSubscribed} APIs subscribed
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="h4"
                sx={{
                  color: "#020817",
                  fontSize: "2.5rem",
                  whiteSpace: "nowrap",
                  fontWeight: 700,
                }}
              >
                {activeMenuLabel}
              </Typography>
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={2} alignItems="center">
          {/* {role?.toLowerCase() !== "vkyc auditor" && (
              <Chip
                label={`Credit Balance: ${remainingCredits}`}
                color="primary"
                variant="outlined"
                sx={{
                  fontWeight: "medium",
                  fontSize: "12px",
                  display: { xs: "none", sm: "flex" },
                }}
              />
            )} */}
            <Avatar
              sx={{
                bgcolor: theme?.palette?.background?.profileIcon,
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 400,
              }}
              {...stringAvatar(user?.username ? user.username : "", theme)}
              onClick={handleClick}
            />
          </Stack>

          <Menu
            elevation={1}
            id="basic-menu"
            anchorEl={anchorEl}
            open={open_menu}
            onClose={handleClose}
            keepMounted
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            {USER_MENU.map((item) => {
              if (item.divider) {
                return <Divider key={item.key} sx={{ width: "100%" }} />;
              }

              const isAdminBilling =
                item.key === "billing" && user?.role?.toLowerCase() === "admin";
              const isVisible = item.key !== "billing" || isAdminBilling;

              if (isVisible) {
                return (
                  <CustomMenuItem
                    key={item.key}
                    onClick={() => {
                      setAnchorEl(null);
                      item.onClick ? item.onClick() : navigate(item.to);
                    }}
                  >
                    <Box component="img" src={item.image} />
                    <Typography variant="h4">{item.label}</Typography>
                  </CustomMenuItem>
                );
              }

              return null;
            })}
          </Menu>
        </Toolbar>
      </Wrapper>
    </AppBar>
  );
};